import { useProductConfigs } from 'src/hooks/useProductConfigs';
import { ProductConfig } from 'src/redux/reducers/productConfigReducer';
import { useAccountApproved } from './useAccountApproved';
import { useEnv } from './useEnv';
import { useProductStatus } from './useProductStatus';

/** @deprecated use useProductActiveV2 instead */
export const useProductActive = () => {
  const { isProdEnv } = useEnv();
  const isAccountApproved = useAccountApproved();
  const { data: productConfigs } = useProductConfigs();

  const { isAcceptanceApproved, isDisbursementApproved } = useProductStatus();

  const offlineDashboardIndomaretCashInActiveParameter =
    productConfigs.find(
      (config: ProductConfig) =>
        config.service === 'DASHBOARD_OFFLINE_INDOMARET' &&
        config.parameter === 'IS_CASH_IN_ENABLED',
    ) || false;

  const offlineDashboardAlfamartCashInActiveParameter =
    productConfigs.find(
      (config: ProductConfig) =>
        config.service === 'DASHBOARD_OFFLINE_ALFAMART' &&
        config.parameter === 'IS_CASH_IN_ENABLED',
    ) || false;

  const dailyWithdrawalConfig = productConfigs.find(
    (config: ProductConfig) =>
      config.service === 'DAILY_WITHDRAWAL' && config.parameter === 'IS_ACTIVE',
  );

  const isDailyWithdrawal =
    dailyWithdrawalConfig?.value.toLowerCase() === 'true' || false;

  const isSubAccountActive =
    productConfigs
      .find(
        config =>
          config.service === 'SUB_ACCOUNT' && config.parameter === 'IS_ACTIVE',
      )
      ?.value.toLowerCase() === 'true' || false;

  const isLongTermPreference =
    productConfigs
      .find(
        (config: ProductConfig) =>
          config.service === 'DAILY_WITHDRAWAL' &&
          config.parameter === 'IS_LONG_TERM',
      )
      ?.value.toLowerCase() === 'true' || false;

  const virtualAccountParameter =
    productConfigs.find(
      (config: ProductConfig) =>
        config.service === 'API_STATIC_VA' && config.parameter === 'IS_ACTIVE',
    ) || false;

  const ewalletAggregatorParameter =
    productConfigs.find(
      (config: ProductConfig) =>
        config.service === 'EWALLET_AGGREGATOR' &&
        config.parameter === 'IS_ACTIVE',
    ) || false;

  const isInternationalRemitActive =
    productConfigs
      .find(
        (config: ProductConfig) =>
          config.service === 'INTERNATIONAL_TRANSFER' &&
          config.parameter === 'IS_ACTIVE',
      )
      ?.value.toLowerCase() === 'true';

  const isOverdraftActive =
    productConfigs
      .find(
        config =>
          config.service === 'API_DISBURSE' &&
          config.parameter === 'OVERDRAFT_IS_ACTIVE',
      )
      ?.value.toLowerCase() === 'true';

  const isActiveConfigPaymentCheckoutProduct =
    productConfigs
      .find(
        (config: ProductConfig) =>
          config.service === 'PAYMENT_CHECKOUT' &&
          config.parameter === 'IS_ACTIVE',
      )
      ?.value.toLowerCase() === 'true';
  const isCardEnabledConfigPaymentCheckoutProduct =
    productConfigs
      .find(
        (config: ProductConfig) =>
          config.service === 'PAYMENT_CHECKOUT' &&
          config.parameter === 'IS_CARD_DISABLED',
      )
      ?.value.toLowerCase() === 'false';

  const vccFee = parseInt(
    productConfigs.find((config: ProductConfig) => {
      return (
        config.service === 'VIRTUAL_CREDIT_CARD' &&
        config.parameter === 'MONTHLY_PRICING'
      );
    })?.value || '',
  );

  /**
   * @deprecated use checkActive('VIRTUAL_CREDIT_CARD') from useProductActiveV2
   */
  const isVirtualCreditCardActive =
    productConfigs
      .find((config: ProductConfig) => {
        return (
          config.service === 'VIRTUAL_CREDIT_CARD' &&
          config.parameter === 'IS_ACTIVE'
        );
      })
      ?.value.toLowerCase() === 'true' &&
    !isNaN(vccFee) &&
    vccFee >= 0;

  // Strictly used for VCC Cashback Logic.
  const isVccActive =
    productConfigs
      .find((config: ProductConfig) => {
        return (
          config.service === 'VIRTUAL_CREDIT_CARD' &&
          config.parameter === 'IS_ACTIVE'
        );
      })
      ?.value.toLowerCase() === 'true';

  /**
   * @deprecated use checkActive('VCC_USE_BRIDGE_FUND') from useProductActiveV2
   */
  const isVccUseBridgeFundActive =
    productConfigs
      .find((config: ProductConfig) => {
        return (
          config.service === 'VIRTUAL_CREDIT_CARD' &&
          config.parameter === 'USE_BRIDGE_FUND'
        );
      })
      ?.value.toLowerCase() === 'true';

  const isVccPriceConfigActive =
    productConfigs
      .find((config: ProductConfig) => {
        return (
          config.service === 'VIRTUAL_CREDIT_CARD' &&
          config.parameter === 'FEE_ACTIVATION'
        );
      })
      ?.value.toUpperCase() != 'NOT_ACTIVE';

  const isVccOverbookingActive =
    productConfigs
      .find((config: ProductConfig) => {
        return (
          config.service === 'VIRTUAL_CREDIT_CARD' &&
          config.parameter === 'IS_OVERBOOKING'
        );
      })
      ?.value.toUpperCase() === 'true';

  const isVccCashbackEnabled =
    productConfigs.find((config: ProductConfig) => {
      return (
        config.service === 'VIRTUAL_CREDIT_CARD' &&
        config.parameter === 'IS_CASHBACK'
      );
    })?.value === 'true';

  const accountPayableActiveParameter =
    productConfigs.find(
      (config: ProductConfig) =>
        config.service === 'ACCOUNT_PAYABLE' &&
        config.parameter === 'IS_ACTIVE',
    ) || false;

  const accountReceivableActiveParameter =
    productConfigs.find(
      (config: ProductConfig) =>
        config.service === 'ACCOUNT_RECEIVABLE' &&
        config.parameter === 'IS_ACTIVE',
    ) || false;

  const accountReceivableWhatsAppActiveParameter =
    productConfigs.find(
      (config: ProductConfig) =>
        config.service === 'ACCOUNT_RECEIVABLE' &&
        config.parameter === 'IS_WHATSAPP_ACTIVE',
    ) || false;

  const accountReceivableIsDemoParameter =
    productConfigs.find(
      (config: ProductConfig) =>
        config.service === 'ACCOUNT_RECEIVABLE' &&
        config.parameter === 'IS_DEMO',
    ) || false;

  const accountPayableIsDemoParameter =
    productConfigs.find(
      (config: ProductConfig) =>
        config.service === 'ACCOUNT_PAYABLE' && config.parameter === 'IS_DEMO',
    ) || false;

  const accountReceivableDemoLimit = parseInt(
    productConfigs.find((config: ProductConfig) => {
      return (
        config.service === 'ACCOUNT_RECEIVABLE' &&
        config.parameter === 'CREATE_INVOICE_LIMIT'
      );
    })?.value || '0',
  );

  const accountPayableDemoLimit = parseInt(
    productConfigs.find((config: ProductConfig) => {
      return (
        config.service === 'ACCOUNT_PAYABLE' &&
        config.parameter === 'CREATE_INVOICE_LIMIT'
      );
    })?.value || '0',
  );

  /**
   * @deprecated use checkActive('OVERDRAFT') from useProductActiveV2
   */
  const isOverdraftProductActive = isProdEnv
    ? isAccountApproved && isDisbursementApproved && isOverdraftActive
    : false;

  /**
   * @deprecated use checkActive('INTERNATIONAL_TRANSFER') from useProductActiveV2
   */
  const isInternationalRemitProductActive =
    isAccountApproved && isDisbursementApproved && isInternationalRemitActive;

  /**
   * @deprecated use checkActive('PAYMENT_CHECKOUT') from useProductActiveV2
   */
  const isPaymentCheckoutProductActive =
    isAccountApproved &&
    isAcceptanceApproved &&
    isActiveConfigPaymentCheckoutProduct;

  /**
   * @deprecated use checkActive('VIRTUAL_ACCOUNT') from useProductActiveV2
   */
  const isVirtualAccountProductActive =
    isAccountApproved &&
    isAcceptanceApproved &&
    virtualAccountParameter &&
    virtualAccountParameter.value.toLowerCase() === 'true';

  /**
   * @deprecated use checkActive('EWALLET_AGGREGATOR') from useProductActiveV2
   */
  const isEwalletAggregatorProductActive =
    isAccountApproved &&
    isAcceptanceApproved &&
    ewalletAggregatorParameter &&
    ewalletAggregatorParameter.value.toLowerCase() === 'true';

  /**
   * @deprecated use checkActive('PAYMENT_ROUTING') from useProductActiveV2
   */
  const isPaymentRoutingProductActive =
    isPaymentCheckoutProductActive || isVirtualAccountProductActive;

  const createVaDashboardParameter =
    productConfigs.find(
      (config: ProductConfig) =>
        config.service === 'API_STATIC_VA' &&
        config.parameter === 'VA_DASHBOARD_ENABLED',
    ) || false;

  /**
   * @deprecated use checkActive('VA_DASHBOARD') from useProductActiveV2
   */
  const isCreateVaDashboardActive =
    isAccountApproved &&
    (!createVaDashboardParameter ||
      (createVaDashboardParameter &&
        createVaDashboardParameter.value.toLowerCase() === 'true'));

  const MultiAccountParameter =
    productConfigs.find(
      (config: ProductConfig) =>
        config.service === 'MULTI_ACCOUNT_MANAGEMENT' &&
        config.parameter === 'IS_ACTIVE',
    ) || false;

  /**
   * @deprecated use checkActive('MULTI_ACCOUNT_MANAGEMENT') from useProductActiveV2
   */
  const isMultiAccountProductActive =
    isAccountApproved &&
    MultiAccountParameter &&
    MultiAccountParameter.value.toLowerCase() === 'true';

  const ChildMultiApprovalParameter =
    productConfigs.find(
      (config: ProductConfig) =>
        config.service === 'CHILD_MULTI_APPROVAL' &&
        config.parameter === 'IS_ACTIVE',
    ) || false;

  /**
   * @deprecated use checkActive('CASH_DELIVERY') from useProductActiveV2 (#TODO)
   */
  const CashDeliveryActive =
    productConfigs.find(
      (config: ProductConfig) =>
        config.service === 'CASH_DELIVERY' && config.parameter === 'IS_ACTIVE',
    ) || false;

  const CashDeliveryEditor =
    productConfigs.find(
      (config: ProductConfig) =>
        config.service === 'CASH_DELIVERY' && config.parameter === 'IS_EDITOR',
    ) || false;

  const isCashDeliveryActive =
    CashDeliveryActive && CashDeliveryActive.value.toLowerCase() === 'true';

  const isCashDeliveryEditor =
    isCashDeliveryActive &&
    CashDeliveryEditor &&
    CashDeliveryEditor.value.toLowerCase() === 'true';

  const isChildMultiApprovalProductActive =
    isAccountApproved &&
    ChildMultiApprovalParameter &&
    ChildMultiApprovalParameter.value.toLowerCase() === 'true';

  const MamMandatoryParentApprovalParameter =
    productConfigs.find(
      (config: ProductConfig) =>
        config.service === 'MULTI_ACCOUNT_MANAGEMENT' &&
        config.parameter === 'MANDATORY_PARENT_APPROVAL',
    ) || false;

  const isMamMandatoryParentApprovalActive =
    MamMandatoryParentApprovalParameter &&
    MamMandatoryParentApprovalParameter.value.toLowerCase() === 'true';

  /**
   * @deprecated use checkActive('CASH_DEPOSIT') from useProductActiveV2
   */
  const isCashInIndomaretActive =
    isAcceptanceApproved &&
    offlineDashboardIndomaretCashInActiveParameter &&
    offlineDashboardIndomaretCashInActiveParameter.value.toLowerCase() ===
      'true';

  /**
   * @deprecated use checkActive('CASH_DEPOSIT') from useProductActiveV2
   */
  const isCashInAlfamartActive =
    isAcceptanceApproved &&
    offlineDashboardAlfamartCashInActiveParameter &&
    offlineDashboardAlfamartCashInActiveParameter.value.toLowerCase() ===
      'true';

  const BridgedParameter =
    productConfigs.find(
      (config: ProductConfig) =>
        config.service === 'API_DISBURSE' &&
        config.parameter === 'BRIDGED_IS_ACTIVE',
    ) || false;

  const isTaxIncluded =
    productConfigs.find(
      (config: ProductConfig) =>
        config.service === 'TAX' &&
        config.parameter === 'TAX_ADMIN_FEE_TYPE' &&
        config.value.toLowerCase() === 'include',
    ) || false;

  // TODO @KillerQueen59: match the service and parameter with BE
  const isBulkDisburseDirectPaymentActive =
    productConfigs.find(
      (config: ProductConfig) =>
        config.service === 'BULK_DISBURSE' &&
        config.parameter === 'BULK_DISBURSEMENT_DIRECT_PAYMENT',
    ) || false;

  const isIntegrationActive = isProdEnv;

  /**
   * @deprecated use checkActive('BRIDGED') from useProductActiveV2
   */
  const isBridgedActive =
    isAccountApproved &&
    isDisbursementApproved &&
    BridgedParameter &&
    BridgedParameter.value.toLowerCase() === 'true';

  /**
   * @deprecated use checkActive('ACCOUNT_PAYABLE') from useProductActiveV2
   */
  const isAccountPayableProductActive =
    isAccountApproved &&
    isDisbursementApproved &&
    accountPayableActiveParameter &&
    accountPayableActiveParameter.value.toLowerCase() === 'true';
  const isAccountPayableCardPaymentActive =
    isActiveConfigPaymentCheckoutProduct &&
    isCardEnabledConfigPaymentCheckoutProduct;

  /**
   * @deprecated use checkActive('ACCOUNT_RECEIVABLE') from useProductActiveV2
   */
  const isAccountReceivableProductActive =
    isAccountApproved &&
    isAcceptanceApproved &&
    accountReceivableActiveParameter &&
    accountReceivableActiveParameter.value.toLowerCase() === 'true';
  const isAccountReceivableWhatsAppActive =
    isAccountApproved &&
    accountReceivableWhatsAppActiveParameter &&
    accountReceivableWhatsAppActiveParameter.value.toLocaleLowerCase() ===
      'true';

  const isAccountReceivableDemo =
    !isAccountReceivableProductActive &&
    accountReceivableIsDemoParameter &&
    accountReceivableIsDemoParameter.value.toLowerCase() === 'true';

  const isAccountPayableDemo =
    !isAccountPayableProductActive &&
    accountPayableIsDemoParameter &&
    accountPayableIsDemoParameter.value.toLowerCase() === 'true';

  const isAccountPayableNew =
    !isAccountPayableProductActive && isAccountPayableDemo;
  const isAccountReceivableNew =
    !isAccountReceivableProductActive && isAccountReceivableDemo;

  const isVaAggregatorActive =
    isPaymentRoutingProductActive && isVirtualAccountProductActive;

  return {
    isBulkDisburseDirectPaymentActive,
    isDailyWithdrawal,
    isLongTermPreference,
    isOverdraftProductActive,
    isPaymentRoutingProductActive,
    isPaymentCheckoutProductActive,
    isVirtualAccountProductActive,
    isEwalletAggregatorProductActive,
    isCreateVaDashboardActive,
    isMultiAccountProductActive,
    isCashInIndomaretActive,
    isCashInAlfamartActive,
    isChildMultiApprovalProductActive,
    isBridgedActive,
    isMamMandatoryParentApprovalActive,
    isAccountPayableProductActive,
    isAccountPayableCardPaymentActive,
    isTaxIncluded,
    isIntegrationActive,
    isAccountReceivableProductActive,
    isAccountReceivableWhatsAppActive,
    isVirtualCreditCardActive,
    isVccPriceConfigActive,
    isInternationalRemitProductActive,
    isAccountReceivableDemo,
    isAccountPayableDemo,
    isAccountReceivableNew,
    isAccountPayableNew,
    accountReceivableDemoLimit,
    accountPayableDemoLimit,
    isVaAggregatorActive,
    dailyWithdrawalConfig,
    vccFee,
    isVccActive,
    isVccUseBridgeFundActive,
    isCashDeliveryActive,
    isCashDeliveryEditor,
    isVccOverbookingActive,
    isVccCashbackEnabled,
    isSubAccountActive,
  };
};
